import { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Copy, Check, ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { getPaymentIntent } from "@/lib/api";
import { copyText } from "@/lib/utils";
import { CHAIN_LABELS } from "@/lib/format";
import { CoinIcon } from "@/components/CoinIcon";
import { toast } from "sonner";

function qrPayload(intent) {
  if (intent.uri) return intent.uri;
  const { chain, treasury, exact_amount } = intent;
  if (chain === "bitcoin") return `bitcoin:${treasury}?amount=${exact_amount}`;
  if (chain === "dogecoin") return `dogecoin:${treasury}?amount=${exact_amount}`;
  return treasury;
}

function CopyRow({ label, value, testid, mono = true, big = false }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const ok = await copyText(String(value));
    if (ok) {
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("Copy failed");
    }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <button
        onClick={copy} data-testid={testid}
        className="mt-1 flex w-full items-center justify-between gap-2 rounded-xl border border-white/15 bg-black px-3 py-2.5 text-left transition-colors hover:border-white/40"
      >
        <span className={`${mono ? "font-mono" : ""} ${big ? "text-lg font-bold" : "text-xs"} break-all text-white`}>{value}</span>
        {copied ? <Check className="h-4 w-4 shrink-0 text-positive" /> : <Copy className="h-4 w-4 shrink-0 text-zinc-500" />}
      </button>
    </div>
  );
}

export default function DepositPanel({ intent: initial, onConfirmed, onBack }) {
  const [intent, setIntent] = useState(initial);
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef(null);
  const confirmedRef = useRef(false);

  // countdown
  useEffect(() => {
    const tick = () => {
      const ms = new Date(intent.expires_at).getTime() - Date.now();
      setRemaining(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [intent.expires_at]);

  const poll = useCallback(async () => {
    try {
      const upd = await getPaymentIntent(intent.id);
      setIntent(upd);
      if (upd.status === "confirmed" && !confirmedRef.current) {
        confirmedRef.current = true;
        if (upd.result) onConfirmed(upd.result);
      }
    } catch (e) { /* keep polling */ }
  }, [intent.id, onConfirmed]);

  // poll every 5s while pending
  useEffect(() => {
    if (intent.status !== "pending") return;
    timerRef.current = setInterval(poll, 5000);
    return () => clearInterval(timerRef.current);
  }, [intent.status, poll]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const expired = intent.status === "expired" || (intent.status === "pending" && remaining <= 0);

  if (intent.status === "confirmed") {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-14 text-center" data-testid="deposit-confirmed">
        <CheckCircle2 className="h-12 w-12 text-positive" strokeWidth={1.5} />
        <div className="font-display text-xl font-black">Payment received</div>
        <div className="text-sm text-zinc-400">Your contribution has been credited on the leaderboard.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-5 py-5" data-testid="deposit-panel">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white" data-testid="deposit-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Change amount / coin
      </button>

      {expired ? (
        <div className="rounded-xl border border-negative/30 bg-negative/[0.06] px-4 py-6 text-center" data-testid="deposit-expired">
          <div className="font-display text-lg font-black text-negative">Request expired</div>
          <div className="mt-1 text-sm text-zinc-400">The 60-minute window elapsed. Go back and create a new request.</div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-2 rounded-full border border-gold/25 bg-gold/[0.06] px-4 py-1.5 text-sm font-bold text-gold" data-testid="deposit-countdown">
            <Clock className="h-4 w-4" /> Expires in {mm}:{ss}
          </div>

          <div className="flex items-center justify-center gap-2 text-center">
            <CoinIcon symbol={intent.symbol} size={22} />
            <span className="text-sm text-zinc-400">
              Send exactly this amount of <b className="text-white">{intent.symbol}</b> on <b className="text-white">{CHAIN_LABELS[intent.chain] || intent.chain}</b>
            </span>
          </div>

          <div className="flex justify-center">
            <div className="rounded-2xl bg-white p-3">
              <QRCodeCanvas value={qrPayload(intent)} size={168} includeMargin={false} data-testid="deposit-qr" />
            </div>
          </div>

          <CopyRow label={`Exact amount (${intent.symbol})`} value={intent.exact_amount} testid="deposit-copy-amount" big />
          <CopyRow label={`Treasury address · ${CHAIN_LABELS[intent.chain] || intent.chain}`} value={intent.treasury} testid="deposit-copy-address" />

          <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-[11px] text-zinc-400">
            ⚠️ Send the <b className="text-white">exact</b> amount shown (the extra decimals are your unique payment code). Any other amount won't be matched. Works from any wallet or exchange.
          </div>

          <div className="flex items-center justify-center gap-2 text-xs text-zinc-500" data-testid="deposit-waiting">
            <Loader2 className="h-4 w-4 animate-spin" /> Waiting for your payment on-chain…
          </div>
        </>
      )}
    </div>
  );
}
